<template>
  <div class="maintenance-page min-h-screen flex items-center justify-center px-4">
    <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
      <!-- Icône de maintenance -->
      <div class="mb-6">
        <svg class="mx-auto h-24 w-24 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>

      <!-- Titre -->
      <h1 class="text-3xl font-bold text-gray-900 mb-4">
        Maintenance en cours
      </h1>

      <!-- Message -->
      <p class="text-gray-600 mb-8">
        Notre plateforme est actuellement en maintenance pour améliorer nos services.<br>
        Nous serons de retour très rapidement.
      </p>

      <!-- Informations supplémentaires -->
      <div class="bg-gray-50 rounded-lg p-4 mb-6">
        <p class="text-sm text-gray-500 mb-2">
          Merci de votre patience et de votre compréhension.
        </p>
        <p class="text-sm text-gray-500">
          Pour toute question, contactez notre
          <button type="button" @click="showLogin = !showLogin" class="text-blue-600 hover:underline font-medium">support technique</button>.
        </p>
      </div>

      <!-- Connexion administrateur (repliée derrière « support technique ») -->
      <form v-if="showLogin && !isSuperAdmin" @submit.prevent="submitLogin" class="text-left space-y-3 mb-6">
        <input
          v-model="loginForm.username"
          type="email"
          autocomplete="username"
          placeholder="Adresse email"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <input
          v-model="loginForm.password"
          type="password"
          autocomplete="current-password"
          placeholder="Mot de passe"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
       <!--  <p v-if="loginError" class="text-sm text-red-600">{{ loginError }}</p> -->
        <button
          type="submit"
          :disabled="authStore.loading"
          class="w-full bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-900 disabled:opacity-50 transition-colors text-sm"
        >
          {{ authStore.loading ? 'Connexion...' : 'Se connecter' }}
        </button>
      </form>

      <!-- Bouton de retour (uniquement pour les admins) -->
      <div v-if="isSuperAdmin" class="space-y-3">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p class="text-sm text-blue-700 mb-2">
            Accès administrateur autorisé
          </p>
          <button @click="disableMaintenance"
            class="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm">
            Accéder à l'administration
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-8 pt-6 border-t border-gray-200">
        <p class="text-xs text-gray-400">
          © {{ new Date().getFullYear() }} Capatools. Tous droits réservés.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()

const isSuperAdmin = computed(() => authStore.user?.roles?.is_super_admin ?? false)

const showLogin = ref(false)
const loginForm = reactive({ username: '', password: '' })
const loginError = ref<string | null>(null)

// Seul un super admin passe la maintenance : les autres restent sur cette page
const submitLogin = async () => {
  loginError.value = null
  const ok = await authStore.login(loginForm.username, loginForm.password)
  if (!ok) {
    loginError.value = authStore.error || 'Erreur de connexion'
    return
  }
  if (authStore.user?.roles?.is_super_admin) {
    window.location.href = '/dashboard'
  } else {
    loginError.value = 'Ce compte n\'a pas les droits administrateur.'
  }
}

const disableMaintenance = () => {
  // Rediriger vers le dashboard admin
  window.location.href = '/dashboard'
}
</script>

<style scoped>
/* Même fond que la page de connexion */
.maintenance-page {
  background-color: rgb(44 50 63);
  background-image: url('@/assets/images/BG_capatools.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
</style>